import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Check } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { FormSection } from '@/components/enterprise/FormSection';
import { engApi } from '../engApi';
import { devApi } from './devApi';
import SearchableMaster from '../../mfg/SearchableMaster';
import { buildPersonSectionOptions, buildLocationOptions } from './pickerOptions';
import { ConceptAiDraft } from './ConceptAiDraft';
import { LearningCard } from './LearningCard';
import { CONCEPT_FIELDS, CONCEPT_FIELD_MAP, emptyConcept } from './conceptFields';

// 本文フィールドだけをまとめて描く。案件名・想定コストは並びの都合で個別に置く。
const CONCEPT_BODY_FIELDS = CONCEPT_FIELDS.filter(
  (x) => !['title', 'estimated_cost'].includes(x.key));

export default function DevCreate() {
  const nav = useNavigate();
  const [meta, setMeta] = useState({ development_types: [] });
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    development_type: '', project_mode: 'existing', project_id: '',
    new_project_name: '', new_project_department_id: '',
    owner_user_ids: [], target_completion_date: '',
    target_department_ids: [], intended_users: '', install_location: '',
    concept: emptyConcept(),
  });

  useEffect(() => {
    devApi.meta().then(setMeta).catch(() => {});
    engApi.projects().then((p) => setProjects(p || [])).catch(() => {});
    engApi.members().then((m) => setMembers(m.users || m.members || [])).catch(() => {});
    devApi.departments().then((d) => setDepts(d || [])).catch(() => {});
    devApi.employees().then((e) => setEmployees(e || [])).catch(() => {});
    devApi.mfgLocations().then((l) => setLocations(l || [])).catch(() => {});
  }, []);

  // 候補は「社員マスタ＋部門マスタ」「場所マスタ」。第2の一覧は作らない。
  const userOptions = buildPersonSectionOptions(employees, depts);
  const locationOptions = buildLocationOptions(locations);

  const setC = (k, v) => setF((x) => ({ ...x, concept: { ...x.concept, [k]: v } }));
  const isMfg = f.development_type === 'mfg_equipment';

  // 何が足りないかを常に明示する（ボタンを黙って無効にしない）
  const missing = [
    !f.development_type && '開発の種類',
    ...CONCEPT_FIELDS.filter((x) => x.required)
      .map((x) => !String(f.concept[x.key] || '').trim() && x.label),
    !f.owner_user_ids.length && '担当者',
    !f.target_completion_date && '目標完了日',
    f.project_mode === 'existing' && !f.project_id && 'プロジェクト',
    f.project_mode === 'new' && !f.new_project_name.trim() && '新しいプロジェクト名',
    f.project_mode === 'new' && !f.new_project_department_id && 'プロジェクトの部署',
    isMfg && !f.target_department_ids.length && '導入先の部門・エリア',
    isMfg && !f.intended_users.trim() && '想定利用者・担当課',
  ].filter(Boolean);

  const submit = async () => {
    if (missing.length) {
      toast.error(`未入力の項目があります: ${missing.join('、')}`);
      return;
    }
    setBusy(true);
    try {
      const body = {
        development_type: f.development_type,
        concept: f.concept,
        owner_user_ids: f.owner_user_ids,
        target_completion_date: f.target_completion_date || null,
        target_department_ids: f.target_department_ids,
        intended_users: f.intended_users || null,
        install_location: f.install_location || null,
        ...(f.project_mode === 'existing'
          ? { project_id: f.project_id }
          : { new_project_name: f.new_project_name, new_project_department_id: f.new_project_department_id }),
      };
      const created = await devApi.create(body);
      toast.success('開発案件を登録しました');
      nav(`/engineering/developments/${created.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || '登録に失敗しました');
    }
    setBusy(false);
  };

  return (
    <div className="space-y-5" data-testid="dev-create-page">
      <PageHeader title="新しい開発案件" description="何を、なぜ作るのかを登録します。構想提案は必須です。"
        testid="dev-create-header"
        secondary={
          <Button variant="outline" onClick={() => nav('/engineering/developments')} data-testid="dev-create-back-btn">
            <ArrowLeft size={15} className="mr-1" /> 一覧へ戻る
          </Button>
        }
        primary={
          <Button onClick={submit} disabled={busy} data-testid="dev-create-submit-btn">
            <Check size={15} className="mr-1" /> {busy ? '登録中…' : '登録する'}
          </Button>
        } />

      <LearningCard developmentType={f.development_type} context="create" />

      <ConceptAiDraft developmentType={f.development_type}
        onApply={(patch) => setF((x) => ({ ...x, concept: { ...x.concept, ...patch } }))} />

      {missing.length > 0 && (
        <div className="rounded-lg border border-zinc-300 bg-zinc-50 px-4 py-3" data-testid="dev-create-missing">
          <p className="text-sm text-zinc-800">
            <span className="font-semibold">未入力の項目:</span> {missing.join('、')}
          </p>
        </div>
      )}

      <FormSection title="① 何を開発しますか" description="種類によって、後の登録先（設備・製品）が変わります。"
        testid="dev-create-type-section">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs font-semibold text-zinc-700">開発の種類（必須）</Label>
            <Select value={f.development_type} onValueChange={(v) => setF({ ...f, development_type: v })}>
              <SelectTrigger data-testid="dev-create-type-select"><SelectValue placeholder="選択してください" /></SelectTrigger>
              <SelectContent className="bg-white">
                {meta.development_types.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-700">プロジェクト</Label>
            <div className="flex gap-2">
              <Select value={f.project_mode} onValueChange={(v) => setF({ ...f, project_mode: v })}>
                <SelectTrigger className="w-32" data-testid="dev-create-project-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="existing">既存から選ぶ</SelectItem>
                  <SelectItem value="new">新しく作る</SelectItem>
                </SelectContent>
              </Select>
              {f.project_mode === 'existing' ? (
                <Select value={f.project_id} onValueChange={(v) => setF({ ...f, project_id: v })}>
                  <SelectTrigger className="flex-1" data-testid="dev-create-project-select">
                    <SelectValue placeholder="プロジェクトを選択" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <div className="flex flex-1 gap-2">
                  <Input value={f.new_project_name} placeholder="新しいプロジェクト名"
                    onChange={(e) => setF({ ...f, new_project_name: e.target.value })}
                    data-testid="dev-create-new-project-name" />
                  <Select value={f.new_project_department_id}
                    onValueChange={(v) => setF({ ...f, new_project_department_id: v })}>
                    <SelectTrigger className="w-36" data-testid="dev-create-new-project-dept">
                      <SelectValue placeholder="部署" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <p className="mt-1 text-xs text-zinc-600">プロジェクトはプランナーで一元管理されます。</p>
          </div>
        </div>

        {isMfg && (
          <div className="mt-4 grid gap-3 rounded-lg border border-zinc-300 bg-zinc-50 p-4 sm:grid-cols-3"
            data-testid="dev-create-mfg-section">
            <div>
              <Label className="text-xs font-semibold text-zinc-700">導入先の部門・エリア（必須）</Label>
              <Select value={f.target_department_ids[0] || ''}
                onValueChange={(v) => setF({ ...f, target_department_ids: [v] })}>
                <SelectTrigger data-testid="dev-create-mfg-dept-select">
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-700">想定利用者・担当課（必須）</Label>
              <SearchableMaster testid="dev-create-mfg-users" value={f.intended_users}
                options={userOptions} canWrite={false} valueField="name" allowNone={false}
                onChange={(v) => setF({ ...f, intended_users: v })}
                labels={{ searchPh: 'mfg.owner.searchPh', noMatch: 'mfg.owner.noMatch' }} />
              <p className="mt-1 text-xs text-zinc-600">社員マスタと部門マスタから選びます。</p>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-700">設置予定場所（分かる場合）</Label>
              <SearchableMaster testid="dev-create-mfg-location" value={f.install_location}
                options={locationOptions} canWrite={false} valueField="name"
                onChange={(v) => setF({ ...f, install_location: v })}
                labels={{ searchPh: 'mfg.owner.searchPh', noMatch: 'mfg.owner.noMatch' }} />
              <p className="mt-1 text-xs text-zinc-600">場所マスタから選びます。</p>
            </div>
          </div>
        )}
      </FormSection>

      <FormSection title="② 構想提案（必須）"
        description="この内容が構想提案そのものです。提案書PDFの添付は任意で、登録後に追加できます。"
        testid="dev-create-concept-section">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label className="text-xs font-semibold text-zinc-700">
              {CONCEPT_FIELD_MAP.title.label}（必須）
            </Label>
            <Input value={f.concept.title} onChange={(e) => setC('title', e.target.value)}
              data-testid="dev-create-title-input" />
          </div>
          {CONCEPT_BODY_FIELDS.map((x) => (
            <div key={x.key} className={x.key === 'intended_use' ? '' : 'sm:col-span-1'}>
              <Label className="text-xs font-semibold text-zinc-700">
                {x.label}{x.required ? '（必須）' : '（任意）'}
              </Label>
              <Textarea rows={x.rows} value={f.concept[x.key]}
                onChange={(e) => setC(x.key, e.target.value)}
                data-testid={`dev-create-${x.key}-input`} />
            </div>
          ))}
          <div>
            <Label className="text-xs font-semibold text-zinc-700">担当者（必須）</Label>
            <Select value={f.owner_user_ids[0] || ''}
              onValueChange={(v) => setF({ ...f, owner_user_ids: [v] })}>
              <SelectTrigger data-testid="dev-create-owner-select">
                <SelectValue placeholder="選択してください" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name || m.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-700">目標完了日（必須）</Label>
            <Input type="date" value={f.target_completion_date}
              onChange={(e) => setF({ ...f, target_completion_date: e.target.value })}
              data-testid="dev-create-target-date-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-700">
              {CONCEPT_FIELD_MAP.estimated_cost.label}（任意）
            </Label>
            <Input value={f.concept.estimated_cost} onChange={(e) => setC('estimated_cost', e.target.value)}
              data-testid="dev-create-cost-input" />
          </div>
        </div>
      </FormSection>
    </div>
  );
}
